import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Download, Menu, ShieldCheck, X } from "lucide-react";
import { Link } from "react-router-dom";
import { legalPageList } from "@/data/legalPages";

const brandLogo = "/koineckta-logo-primary.png";

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const renderInline = (text) =>
  text.split(/(\*\*.*?\*\*)/g).map((part, index) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={`${part}-${index}`} className="font-semibold text-slate-950">
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    ),
  );

const parseMarkdown = (markdown) => {
  const nodes = [];
  let paragraph = [];
  let list = [];

  const flushParagraph = () => {
    if (paragraph.length) nodes.push({ type: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = () => {
    if (list.length) nodes.push({ type: "list", items: list });
    list = [];
  };

  markdown.split("\n").forEach((rawLine) => {
    const line = rawLine.trim();
    const heading = line.match(/^(#{2,3})\s+(.+)$/);
    const listItem = line.match(/^[*-]\s+(.+)$/);

    if (!line) {
      flushParagraph();
      flushList();
    } else if (heading) {
      flushParagraph();
      flushList();
      nodes.push({ type: heading[1] === "##" ? "heading" : "subheading", text: heading[2] });
    } else if (listItem) {
      flushParagraph();
      list.push(listItem[1]);
    } else {
      flushList();
      paragraph.push(line);
    }
  });

  flushParagraph();
  flushList();
  return nodes;
};

export default function LegalPage({ document: legalDocument }) {
  const [markdown, setMarkdown] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const nodes = useMemo(() => parseMarkdown(markdown), [markdown]);
  const headings = useMemo(() => nodes.filter((node) => node.type === "heading"), [nodes]);

  useEffect(() => {
    window.document.title = `${legalDocument.title} | Koineckta`;
    window.scrollTo(0, 0);
    setMarkdown("");
    setLoadError(false);
    setMenuOpen(false);
    fetch(legalDocument.contentUrl)
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load legal document");
        return response.text();
      })
      .then(setMarkdown)
      .catch(() => setLoadError(true));
  }, [legalDocument]);

  return (
    <div className="legal-shell min-h-screen text-white" data-testid={`${legalDocument.slug}-page-root`}>
      <header className="sticky top-0 z-40 border-b border-[#3ABDBC]/20 bg-[#010B30]/88 backdrop-blur-xl" data-testid="legal-page-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8 lg:px-10">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-header-home-link">
            <img src={brandLogo} alt="Koineckta" className="h-10 w-auto" data-testid="legal-header-logo" />
          </Link>
          <nav className="hidden items-center gap-6 md:flex" aria-label="Legal pages" data-testid="legal-desktop-navigation">
            {legalPageList.map((page) => (
              <Link
                key={page.slug}
                to={`/${page.slug}`}
                className={`text-sm font-semibold ${page.slug === legalDocument.slug ? "text-sky-700" : "text-slate-600 hover:text-slate-950"}`}
                data-testid={`legal-nav-${page.slug}`}
              >
                {page.navLabel}
              </Link>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 md:hidden"
            aria-label="Toggle legal navigation"
            aria-expanded={menuOpen}
            data-testid="legal-mobile-menu-button"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <nav className="border-t border-slate-100 bg-white px-5 py-4 md:hidden" aria-label="Mobile legal pages" data-testid="legal-mobile-navigation">
            <div className="mx-auto flex max-w-7xl flex-col gap-2">
              {legalPageList.map((page) => (
                <Link key={page.slug} to={`/${page.slug}`} className="px-3 py-2 text-sm font-semibold text-slate-700" data-testid={`legal-mobile-nav-${page.slug}`}>
                  {page.navLabel}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main>
        <section className="legal-hero border-b border-sky-100" data-testid="legal-page-hero">
          <motion.div
            className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20 lg:px-10"
            initial={reduceMotion ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link to="/" className="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-sky-700" data-testid="legal-back-home-link">
              <ArrowLeft className="h-4 w-4" />
              Back to Koineckta
            </Link>
            <div className="max-w-4xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/75 px-4 py-2 text-xs font-semibold uppercase text-sky-700" data-testid="legal-page-eyebrow">
                <ShieldCheck className="h-4 w-4" />
                {legalDocument.eyebrow}
              </div>
              <h1 className="font-display text-4xl font-semibold leading-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="legal-page-title">
                {legalDocument.title}
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg" data-testid="legal-page-description">
                {legalDocument.description}
              </p>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600" data-testid="legal-document-metadata">
                <span data-testid="legal-document-version"><strong className="text-slate-950">Version</strong> {legalDocument.version}</span>
                <span data-testid="legal-document-effective-date"><strong className="text-slate-950">Effective</strong> {legalDocument.effectiveDate}</span>
                <span data-testid="legal-document-updated-date"><strong className="text-slate-950">Updated</strong> {legalDocument.updatedDate}</span>
              </div>
            </div>
          </motion.div>
        </section>

        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[260px_minmax(0,760px)] lg:justify-between lg:px-10 lg:py-20">
          <aside className="hidden lg:block" data-testid="legal-table-of-contents">
            <div className="sticky top-28 border-l-2 border-sky-100 pl-5">
              <p className="mb-4 text-xs font-semibold uppercase text-sky-700">On this page</p>
              <nav className="max-h-[65vh] space-y-2 overflow-y-auto pr-3" aria-label="Document sections">
                {headings.map((heading) => (
                  <a key={heading.text} href={`#${slugify(heading.text)}`} className="block text-sm leading-5 text-slate-500 hover:text-sky-700" data-testid={`legal-toc-${slugify(heading.text)}`}>
                    {heading.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <article className="min-w-0" data-testid="legal-document-content">
            {loadError && (
              <div className="border-l-4 border-red-500 bg-red-50 p-5 text-sm text-red-800" role="alert" data-testid="legal-document-error">
                This document could not be loaded. Please try again shortly or download the original file below.
              </div>
            )}
            {!loadError && !markdown && <p className="text-sm text-slate-500" data-testid="legal-document-loading">Loading document…</p>}
            {nodes.map((node, index) => {
              if (node.type === "heading") {
                const id = slugify(node.text);
                return <h2 key={`${id}-${index}`} id={id} className="scroll-mt-28 border-t border-slate-200 pt-12 font-display text-2xl font-semibold leading-tight text-slate-950 first:border-0 first:pt-0 sm:text-3xl" data-testid={`legal-section-${id}`}>{node.text}</h2>;
              }
              if (node.type === "subheading") {
                return <h3 key={`${node.text}-${index}`} className="mt-8 font-display text-xl font-semibold text-slate-950">{node.text}</h3>;
              }
              if (node.type === "list") {
                return <ul key={`list-${index}`} className="my-5 space-y-2.5 pl-5 text-base leading-7 text-slate-600">{node.items.map((item) => <li key={item} className="list-disc pl-1 marker:text-cyan-500">{renderInline(item)}</li>)}</ul>;
              }
              return <p key={`paragraph-${index}`} className="my-5 text-base leading-8 text-slate-600">{renderInline(node.text)}</p>;
            })}
            <div className="mt-14 border-t border-slate-200 pt-8" data-testid="legal-document-download-section">
              <a href={legalDocument.downloadUrl} download className="inline-flex h-12 items-center gap-2 rounded-full bg-[#6AE32F] px-6 text-sm font-semibold text-[#010B30] hover:bg-[#82EF4D]" data-testid="legal-document-download-link">
                <Download className="h-4 w-4" />
                Download original document
              </a>
            </div>
          </article>
        </div>
      </main>

      <footer className="border-t border-[#3ABDBC]/20 bg-[#010B30]/85" data-testid="legal-page-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 md:flex-row md:items-center md:justify-between lg:px-10">
          <p className="text-sm text-slate-500" data-testid="legal-footer-copyright">© 2026 Koineckta Technologies Limited</p>
          <div className="flex flex-wrap gap-5">
            {legalPageList.map((page) => <Link key={page.slug} to={`/${page.slug}`} className="text-sm font-semibold text-slate-600 hover:text-sky-700" data-testid={`legal-footer-${page.slug}`}>{page.navLabel}</Link>)}
          </div>
        </div>
      </footer>
    </div>
  );
}